import {
  IntegrationApiFeaturesServiceAvailabilityContractsResponsesGetServiceAvailabilityDetailsResponse as ServiceAvailabilityDetailsResponse,
  IntegrationApiFeaturesOutagesContractsResponsesSubscribeSmsServiceResponse as SubscribeSmsServiceResponse,
  IntegrationApiFeaturesOutagesContractsResponsesSubscribeSmsServiceOutageResponse as SubscribeSmsServiceOutageResponse,
  IntegrationApiFeaturesOutagesContractsDataOutage as SpecificOutageResponse,
} from '@alliander-fe/api';
import { UseMutationResult } from '@tanstack/react-query';

import { isOutageCause } from 'src/utils';
import { isResponseSuccess } from './isResponseSuccess';

type GetResponseProps = {
  serviceAvailabilityDetails: UseMutationResult<ServiceAvailabilityDetailsResponse, Error>;
  subscribeSmsService: UseMutationResult<SubscribeSmsServiceResponse, Error>;
  subscribeSmsServiceOutage: UseMutationResult<SubscribeSmsServiceOutageResponse, Error>;
  specificOutageMutation: UseMutationResult<SpecificOutageResponse, Error>;
};

export const handleApiResponses = ({
  serviceAvailabilityDetails,
  subscribeSmsService,
  subscribeSmsServiceOutage,
  specificOutageMutation,
}: GetResponseProps) => {
  const isOperational =
    serviceAvailabilityDetails.data?.electricityNetwork?.isOperational ||
    serviceAvailabilityDetails.data?.gasNetwork?.isOperational;

  const serviceNotFound =
    serviceAvailabilityDetails.isError ||
    (serviceAvailabilityDetails.isSuccess && Boolean(!isOperational));

  const isOutage =
    specificOutageMutation?.data?.cause && isOutageCause(specificOutageMutation?.data?.cause);

  const isLoading =
    serviceAvailabilityDetails.isPending ||
    subscribeSmsService.isPending ||
    subscribeSmsServiceOutage.isPending ||
    specificOutageMutation.isPending;

  const hasError =
    serviceAvailabilityDetails.isError ||
    subscribeSmsServiceOutage.isError ||
    subscribeSmsService.isError ||
    specificOutageMutation.isError;

  const isSuccess =
    serviceAvailabilityDetails.isSuccess &&
    subscribeSmsService.isSuccess &&
    subscribeSmsServiceOutage.isSuccess &&
    specificOutageMutation.isSuccess;

  const isIdle =
    serviceAvailabilityDetails.isIdle &&
    subscribeSmsService.isIdle &&
    subscribeSmsServiceOutage.isIdle &&
    specificOutageMutation.isIdle;

  // We check these both these api's separately because in case of also a future subscription we can
  // return that the Outage is already been solved.
  const futureOutageServiceSubscription = subscribeSmsService.data?.success;
  const currentOutageServiceSubscription = subscribeSmsServiceOutage.data?.success;

  const nrAlreadyExists = isResponseSuccess({
    serviceOne: subscribeSmsService.data?.nrAlreadyExists,
    serviceTwo: subscribeSmsServiceOutage.data?.nrAlreadyExists,
  });

  const tooManyAddresses = isResponseSuccess({
    serviceOne: subscribeSmsService.data?.tooManyAddresses,
    serviceTwo: subscribeSmsServiceOutage.data?.tooManyAddresses,
  });

  const outageNoLongerExists = subscribeSmsServiceOutage.data?.outageNoLongerExists || !isOutage;

  return {
    isOperational,
    serviceNotFound,
    isLoading,
    isSuccess,
    hasError,
    isIdle,
    futureOutageServiceSubscription,
    currentOutageServiceSubscription,
    nrAlreadyExists,
    tooManyAddresses,
    outageNoLongerExists,
  };
};

